import { useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Box, F0Heading, F0Text } from '@factorialco/f0-react'

import type { PurchaseOrderDetailFieldsFragment } from 'generated/resources/graphql'

import MoneyWithCurrency from 'modules/core/lib/money'

import {
  sectionLabel,
  totalsLabel,
} from 'modules/procurement/components/poTemplates/FieldEditor/translations'

import {
  DraftRow,
  Row,
} from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/DetailsTab/LineItemRows'
import type { LineItemDraft } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/useEditPurchaseOrder'
import {
  buildFooterTotals,
  buildFooterTotalsFromDrafts,
  resolveFieldLabel,
} from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/utils'

// ─── Props ──────────────────────────────────────────────────────────────
interface Props {
  purchaseOrder: PurchaseOrderDetailFieldsFragment
  locale: string
  /** When true, footer totals are computed from draft line items instead of server data */
  isEditing?: boolean
  /** Draft line items — used only for footer totals when isEditing */
  lineItems?: LineItemDraft[]
  /** Override currency for display; defaults to purchaseOrder.cost.currency */
  currency?: string
}

// ─── Component ──────────────────────────────────────────────────────────

function LineItemsSection({
  purchaseOrder,
  locale,
  isEditing = false,
  lineItems,
  currency: currencyProp,
}: Props) {
  const columnDefinitions = useMemo(() => {
    const definitions = purchaseOrder.poTemplateVersion?.poTemplateFieldDefinitions ?? []
    return [...definitions]
      .filter((d) => d.sectionType === 'line_item_columns' && d.visible)
      .sort((a, b) => a.position - b.position)
  }, [purchaseOrder.poTemplateVersion])

  const sortedLineItems = useMemo(
    () => [...purchaseOrder.poLineItems].sort((a, b) => a.position - b.position),
    [purchaseOrder.poLineItems]
  )

  const allDefinitions = useMemo(
    () => purchaseOrder.poTemplateVersion?.poTemplateFieldDefinitions ?? [],
    [purchaseOrder.poTemplateVersion]
  )

  const footerTotals = useMemo(() => {
    if (isEditing && lineItems) {
      return buildFooterTotalsFromDrafts(lineItems, allDefinitions, totalsLabel)
    }
    return buildFooterTotals(purchaseOrder, totalsLabel)
  }, [isEditing, lineItems, allDefinitions, purchaseOrder])

  if (columnDefinitions.length === 0) return null

  const currency = currencyProp ?? purchaseOrder.cost.currency
  const gridStyle = {
    display: 'grid' as const,
    gridTemplateColumns: `repeat(${columnDefinitions.length}, minmax(120px, 1fr))`,
  }

  const rowsToRender = isEditing && lineItems ? lineItems : null
  const hasLineItems = rowsToRender ? rowsToRender.length > 0 : sortedLineItems.length > 0

  return (
    <F0Box display="flex" flexDirection="column" gap="sm">
      <F0Heading content={sectionLabel('line_item_columns')} as="h3" />

      <F0Box
        border="default"
        borderColor="secondary"
        borderRadius="sm"
        display="flex"
        flexDirection="column"
        divider="y"
        dividerColor="secondary"
        background="primary"
        overflowX="auto"
      >
        {/* Column headers */}
        <Box style={gridStyle}>
          {columnDefinitions.map((def) => (
            <F0Box key={def.id} paddingX="sm" paddingY="sm">
              <F0Text content={resolveFieldLabel(def)} variant="description" />
            </F0Box>
          ))}
        </Box>
        {/* Data rows */}
        {hasLineItems ? (
          rowsToRender ? (
            rowsToRender.map((draft, idx) => (
              <DraftRow
                key={draft.id ?? `draft-${idx}`}
                draft={draft}
                columnDefinitions={columnDefinitions}
                gridStyle={gridStyle}
                locale={locale}
                currency={currency}
              />
            ))
          ) : (
            sortedLineItems.map((lineItem, idx) => (
              <Row
                key={lineItem.id}
                lineItem={lineItem}
                rowIndex={idx}
                columnDefinitions={columnDefinitions}
                gridStyle={gridStyle}
                locale={locale}
                currency={currency}
              />
            ))
          )
        ) : (
          <Box style={gridStyle}>
            {columnDefinitions.map((def) => (
              <F0Box key={def.id} paddingX="sm" paddingY="sm">
                <F0Text content=" " variant="body" />
              </F0Box>
            ))}
          </Box>
        )}
      </F0Box>

      {/* Footer totals */}
      <F0Box display="flex" gap="xl" justifyContent="end" paddingX="sm">
        {footerTotals.map((total) => {
          const formatted = MoneyWithCurrency({
            amount: total.amountCents,
            currency,
            locale,
          })
          return (
            <F0Box
              key={total.label}
              display="flex"
              flexDirection="column"
              alignItems="end"
              gap="xs"
            >
              <F0Text content={total.label} variant="description" />
              <F0Text content={formatted} variant="body" />
            </F0Box>
          )
        })}
      </F0Box>
    </F0Box>
  )
}

export default LineItemsSection
